import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BookCopy,
  BookOpen,
  ChevronRight,
  Clock3,
  FileStack,
  Filter,
  Layers3,
  Loader2,
  AlertCircle,
  ScrollText,
  Sparkles,
} from 'lucide-react';
import { getApiBaseUrl } from '../lib/api';

interface OutlineItem {
  id: string;
  type: 'standard' | 'three-tier' | 'unknown';
  title: string;
  genre: string;
  targetWordCount: number;
  synopsis: string;
  chapterCount: number;
  plannedChapterCount?: number;
  sourceChapterCount?: number | null;
  volumeCount: number;
  chapterPlanMode?: 'source-book' | 'target-words' | null;
  chapterPlanModeInferred?: boolean;
  hasGoldenChapters: boolean;
  createdAt: string;
}

type HistoryFilter = 'all' | 'three-tier' | 'standard' | 'golden';

const FILTER_OPTIONS: Array<{
  key: HistoryFilter;
  label: string;
  hint: string;
}> = [
  { key: 'all', label: '全部记录', hint: '查看所有拆书结果' },
  { key: 'three-tier', label: '三层级', hint: '优先看卷纲与章纲' },
  { key: 'standard', label: '标准拆书', hint: '快速查看章纲与正文' },
  { key: 'golden', label: '含示范正文', hint: '只看可直接读的记录' },
];

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatWordCount(count: number) {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万字`;
  }
  return `${count}字`;
}

function getTypeLabel(type: OutlineItem['type']) {
  if (type === 'three-tier') return '三层级大纲';
  if (type === 'standard') return '标准拆书';
  return '未知结构';
}

function getTypeTone(type: OutlineItem['type']) {
  if (type === 'three-tier') {
    return 'border-blue-200 bg-blue-50 text-blue-700';
  }

  if (type === 'standard') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  return 'border-slate-200 bg-slate-100 text-slate-600';
}

function getPrimaryEntryLabel(outline: OutlineItem) {
  if (outline.type === 'three-tier') {
    return '查看卷纲与章纲';
  }

  if (outline.hasGoldenChapters) {
    return '查看章纲与示范正文';
  }

  return '查看拆书详情';
}

function getPlannedChapterCount(outline: OutlineItem) {
  return outline.plannedChapterCount || outline.chapterCount || 0;
}

function getPlanModeLabel(outline: OutlineItem) {
  const inferredSuffix = outline.chapterPlanModeInferred ? '（历史记录推断）' : '';

  if (outline.chapterPlanMode === 'source-book') {
    return `按原书章节拆解${inferredSuffix}`;
  }

  if (outline.chapterPlanMode === 'target-words') {
    return `按目标字数重规划${inferredSuffix}`;
  }

  return null;
}

function getPlanExplanation(outline: OutlineItem) {
  const plannedChapterCount = getPlannedChapterCount(outline);
  const sourceChapterCount = outline.sourceChapterCount;
  const inferredPrefix = outline.chapterPlanModeInferred ? '根据历史记录推断，' : '';

  if (outline.type !== 'three-tier') {
    return `这条记录当前保存的是本次生成的 ${plannedChapterCount} 章章纲${outline.hasGoldenChapters ? '，并附带示范正文' : ''}。`;
  }

  if (outline.chapterPlanMode === 'source-book' && typeof sourceChapterCount === 'number' && sourceChapterCount > 0) {
    return `${inferredPrefix}这次是跟着原书真实 ${sourceChapterCount} 章逐章拆出来的，不是重新规划的新书结构。`;
  }

  if (outline.chapterPlanMode === 'target-words' && typeof sourceChapterCount === 'number' && sourceChapterCount > 0) {
    return `${inferredPrefix}这次不是还原原书 ${sourceChapterCount} 章，而是按目标篇幅重新规划成 ${plannedChapterCount} 章的新书结构。`;
  }

  if (outline.chapterPlanMode === 'target-words') {
    return `${inferredPrefix}这次章数是按目标篇幅重新规划出来的，不能当成原书真实章节数。`;
  }

  if (typeof sourceChapterCount === 'number' && sourceChapterCount > 0) {
    return `原书真实有 ${sourceChapterCount} 章，这条历史当前展示的是 ${plannedChapterCount} 章的生成结果。`;
  }

  return `这条历史当前展示的是 ${plannedChapterCount} 章的生成结果。`;
}

export default function HistoryPage() {
  const [outlines, setOutlines] = useState<OutlineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<HistoryFilter>('all');

  useEffect(() => {
    void fetchOutlines();
  }, []);

  const fetchOutlines = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${getApiBaseUrl()}/api/outlines?limit=50`);
      const data = await res.json();

      if (data.success) {
        setOutlines(data.outlines || []);
      } else {
        setError(data.error?.message || '获取历史记录失败');
      }
    } catch {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const totalCount = outlines.length;
  const threeTierCount = outlines.filter((item) => item.type === 'three-tier').length;
  const standardCount = outlines.filter((item) => item.type === 'standard').length;
  const goldenCount = outlines.filter((item) => item.hasGoldenChapters).length;

  const filteredOutlines = outlines.filter((outline) => {
    if (activeFilter === 'three-tier') {
      return outline.type === 'three-tier';
    }
    if (activeFilter === 'standard') {
      return outline.type === 'standard';
    }
    if (activeFilter === 'golden') {
      return outline.hasGoldenChapters;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(252,211,77,0.18),transparent_28%),radial-gradient(circle_at_top_right,rgba(59,130,246,0.14),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#eef2ff_45%,#f8fafc_100%)] px-4 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/85 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-sm"
        >
          <div className="grid gap-6 lg:grid-cols-[1.4fr_0.9fr]">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold tracking-[0.2em] text-amber-700">
                <BookCopy className="h-3.5 w-3.5" />
                拆书档案馆
              </div>
              <div>
                <h1 className="font-serif text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                  历史记录现在是可筛选的拆书工作台
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">
                  这里不再只是结果列表。你可以按三层级、标准拆书、示范正文快速过滤，并直接进入卷纲、章纲、正文的可视化详情。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                  <Layers3 className="h-4 w-4 text-blue-500" />
                  三层级优先看卷纲
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                  <ScrollText className="h-4 w-4 text-emerald-500" />
                  标准拆书直接看示范正文
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                  <Clock3 className="h-4 w-4 text-amber-500" />
                  最近 50 条历史记录
                </span>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,rgba(15,23,42,0.96),rgba(30,41,59,0.92))] p-5 text-white shadow-[0_18px_50px_rgba(15,23,42,0.24)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] text-sky-200">默认推荐</p>
                  <h2 className="mt-2 text-2xl font-semibold">直接走三层级拆书</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-300">
                    现在 V3 页默认优先生成总纲、卷纲、章纲。长篇连载场景下，历史页会更适合回看和复用。
                  </p>
                </div>
                <Sparkles className="h-8 w-8 shrink-0 text-sky-300" />
              </div>

              <div className="mt-6 space-y-3">
                <Link
                  to="/v3"
                  className="flex items-center justify-between rounded-2xl bg-sky-400 px-4 py-3 text-sm font-semibold text-slate-950 transition-transform hover:-translate-y-0.5"
                >
                  <span>进入 V3，默认生成三层级</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="text-xs leading-6 text-slate-400">
                  如果只想快速看章纲和黄金三章，也可以在生成页切回“标准拆书”。
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
        >
          {[
            {
              label: '总记录数',
              value: `${totalCount}`,
              hint: '当前历史库总量',
              accent: 'from-slate-900 to-slate-700',
            },
            {
              label: '三层级记录',
              value: `${threeTierCount}`,
              hint: '可直接查看卷纲与章纲',
              accent: 'from-blue-600 to-cyan-500',
            },
            {
              label: '标准拆书',
              value: `${standardCount}`,
              hint: '适合快速回看章纲',
              accent: 'from-emerald-600 to-teal-500',
            },
            {
              label: '含示范正文',
              value: `${goldenCount}`,
              hint: '可直接读黄金三章',
              accent: 'from-amber-500 to-orange-500',
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-[24px] border border-white/60 bg-white/80 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-sm"
            >
              <div className={`inline-flex rounded-full bg-gradient-to-r px-3 py-1 text-xs font-semibold text-white ${card.accent}`}>
                {card.label}
              </div>
              <div className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">{card.value}</div>
              <p className="mt-2 text-sm text-slate-500">{card.hint}</p>
            </div>
          ))}
        </motion.section>

        {loading ? (
          <div className="flex items-center justify-center rounded-[24px] border border-slate-200 bg-white/80 py-20 shadow-[0_18px_50px_rgba(15,23,42,0.05)]">
            <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
            <span className="ml-3 text-slate-600">正在加载历史记录...</span>
          </div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3 rounded-[24px] border border-red-200 bg-red-50 px-5 py-4 text-red-700 shadow-[0_18px_40px_rgba(239,68,68,0.08)]"
          >
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </motion.div>
        ) : (
          <>
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="rounded-[24px] border border-slate-200 bg-white/82 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Filter className="h-4 w-4 text-slate-500" />
                    筛选视图
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    当前显示 {filteredOutlines.length} 条记录。优先切到“三层级”可以快速回看卷纲。
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {FILTER_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      onClick={() => setActiveFilter(option.key)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                        activeFilter === option.key
                          ? 'border-slate-900 bg-slate-900 text-white shadow-[0_10px_25px_rgba(15,23,42,0.18)]'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.section>

            {filteredOutlines.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-6 py-16 text-center shadow-[0_18px_50px_rgba(15,23,42,0.04)]"
              >
                <BookOpen className="mx-auto h-14 w-14 text-slate-300" />
                <h2 className="mt-4 text-xl font-semibold text-slate-900">当前筛选下还没有记录</h2>
                <p className="mt-2 text-sm text-slate-500">
                  你可以切换筛选条件，或者直接去 V3 生成新的三层级拆书结果。
                </p>
                <Link
                  to="/v3"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                >
                  进入 V3
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {filteredOutlines.map((outline, index) => (
                  <motion.div
                    key={outline.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                  >
                    <Link
                      to={`/history/${outline.id}`}
                      className="group block rounded-[28px] border border-slate-200 bg-white/84 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)] transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_24px_60px_rgba(15,23,42,0.1)]"
                    >
                      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${getTypeTone(outline.type)}`}>
                              {getTypeLabel(outline.type)}
                            </span>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                              {outline.genre}
                            </span>
                            {outline.hasGoldenChapters && (
                              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                                含示范正文
                              </span>
                            )}
                          </div>

                          <div className="mt-4 flex items-start gap-3">
                            <div className={`mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                              outline.type === 'three-tier' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'
                            }`}>
                              {outline.type === 'three-tier' ? (
                                <Layers3 className="h-5 w-5" />
                              ) : (
                                <FileStack className="h-5 w-5" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                                    {outline.title}
                                  </h3>
                                  <p className="mt-2 line-clamp-2 text-sm leading-7 text-slate-600">
                                    {outline.synopsis}
                                  </p>
                                </div>
                                <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-slate-500" />
                              </div>

                              <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
                                {typeof outline.sourceChapterCount === 'number' && outline.sourceChapterCount > 0 ? (
                                  <span className="rounded-full bg-slate-100 px-3 py-1">
                                    原书 {outline.sourceChapterCount} 章
                                  </span>
                                ) : null}
                                <span className="rounded-full bg-slate-100 px-3 py-1">
                                  本次生成 {getPlannedChapterCount(outline)} 章
                                </span>
                                <span className="rounded-full bg-slate-100 px-3 py-1">
                                  {outline.volumeCount > 0 ? `${outline.volumeCount}卷` : '未拆卷'}
                                </span>
                                {getPlanModeLabel(outline) && (
                                  <span className="rounded-full bg-slate-100 px-3 py-1">
                                    {getPlanModeLabel(outline)}
                                  </span>
                                )}
                                <span className="rounded-full bg-slate-100 px-3 py-1">
                                  {formatWordCount(outline.targetWordCount)}
                                </span>
                                <span className="rounded-full bg-slate-100 px-3 py-1">
                                  {formatDate(outline.createdAt)}
                                </span>
                              </div>

                              <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm leading-6 text-amber-900">
                                {getPlanExplanation(outline)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="w-full shrink-0 rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 lg:w-72">
                          <p className="text-xs font-semibold tracking-[0.18em] text-slate-400">推荐入口</p>
                          <p className="mt-2 text-sm font-semibold text-slate-900">
                            {getPrimaryEntryLabel(outline)}
                          </p>
                          <p className="mt-2 text-sm leading-6 text-slate-500">
                            {outline.type === 'three-tier'
                              ? '这条记录更适合看结构拆分，卷纲与章纲都已经在详情页里完整展开。'
                              : outline.hasGoldenChapters
                                ? '这条记录适合直接读章纲和黄金三章，适合快速判断生成质量。'
                                : '这条记录可回看核心章纲与拆书摘要。'}
                          </p>

                          <div className="mt-4 flex flex-wrap gap-2">
                            {outline.type === 'three-tier' && (
                              <>
                                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">卷纲</span>
                                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">章纲</span>
                              </>
                            )}
                            {outline.type === 'standard' && (
                              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">章纲</span>
                            )}
                            {outline.hasGoldenChapters && (
                              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">示范正文</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
